import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Feature, FeatureCollection } from "@turf/turf";
import { FieldsIcon, HectaresIcon } from "common/icons";
import { useTranslation } from "common/locales";
import {
  getAllFieldsForSilage,
  getArableFieldMetrics,
  getArableFields,
  getGrainFieldMetrics,
  getGrainFields,
  getSilageFieldMetrics,
  setIsEditing,
  useAppDispatch,
  useAppSelector,
} from "model";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { mixpanel } from "../../common/analytics";
import FieldMapPreview from "./FieldMapPreview";

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 8px 36px;
  margin: 0 -36px;
  gap: 16px;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const FieldCard = styled(Card)`
  min-width: 280px;
  max-width: 320px;
  cursor: pointer;
  transition: transform 0.2s;
  background-color: white;
  border-radius: 16px;

  &:hover {
    transform: translateY(-2px);
  }
`;

const MapPreviewContainer = styled.div`
  height: 120px;
  background: #f5f5f5;
  border-radius: 16px 16px 0 0;
  position: relative;
  overflow: hidden;
  margin: -16px -16px 16px -16px;
`;

const MapPlaceholderText = styled(Typography)`
  font-weight: 500;
  letter-spacing: 0.5px;
  opacity: 0.8;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const MetricsContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddMoreButton = styled(Button)`
  color: #2e7831;
  text-transform: none;
`;

interface BaseField {
  guid: string;
  geom: Feature;
}

interface FieldMetrics {
  fieldCount: number;
  totalArea: number;
  totalDryMatter?: number;
  growthRate?: number;
  totalCover?: number;
}

interface FieldTypeCardProps {
  title: string;
  fields: BaseField[];
  metrics: FieldMetrics;
  onCardClick: () => void;
}

const FieldTypeCard: React.FC<FieldTypeCardProps> = ({
  title,
  fields,
  metrics,
  onCardClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const iconSize = isMobile ? "small" : "medium";

  const fieldGeojson: FeatureCollection = {
    type: "FeatureCollection",
    features: fields.map((field) => field.geom),
  };

  const isArable =
    title === t("fieldTypeCards.grain.title") ||
    title === t("fieldTypeCards.arable.title");

  const renderMetrics = () => {
    switch (title) {
      case t("fieldTypeCards.grain.title"):
        return (
          <MetricsContainer>
            <MetricRow>
              <FieldsIcon fontSize={iconSize} />
              <Typography variant="body2">
                {metrics.fieldCount} {t("fieldTypeCards.metrics.fields")}
              </Typography>
            </MetricRow>
            <MetricRow>
              <HectaresIcon fontSize={iconSize} />
              <Typography variant="body2">
                {t("fieldTypeCards.metrics.hectares")}: {metrics.totalArea}
              </Typography>
            </MetricRow>
          </MetricsContainer>
        );
      case t("fieldTypeCards.arable.title"):
        return (
          <MetricsContainer>
            <MetricRow>
              <FieldsIcon fontSize={iconSize} />
              <Typography variant="body2">
                {metrics.fieldCount} {t("fieldTypeCards.metrics.fields")}
              </Typography>
            </MetricRow>
            <MetricRow>
              <HectaresIcon fontSize={iconSize} />
              <Typography variant="body2">
                {t("fieldTypeCards.metrics.hectares")}: {metrics.totalArea}
              </Typography>
            </MetricRow>
          </MetricsContainer>
        );
      case t("fieldTypeCards.silage.title"):
        return (
          <MetricsContainer>
            <MetricRow>
              <FieldsIcon fontSize={iconSize} />
              <Typography variant="body2">
                {metrics.fieldCount} {t("fieldTypeCards.metrics.fields")}
              </Typography>
            </MetricRow>
            <MetricRow>
              <HectaresIcon fontSize={iconSize} />
              <Typography variant="body2">
                {t("fieldTypeCards.metrics.hectares")}: {metrics.totalArea}
              </Typography>
            </MetricRow>
            <MetricColumn>
              <Typography variant="body2">
                {t("fieldTypeCards.metrics.dryMatterAvailable")}:{" "}
                {metrics.totalDryMatter}{" "}
                {t("fieldTypeCards.metrics.dryMatterAvailableUnit")}
              </Typography>
            </MetricColumn>
          </MetricsContainer>
        );
      default:
        return null;
    }
  };

  return (
    <FieldCard onClick={onCardClick}>
      <CardContent>
        <MapPreviewContainer>
          {fields.length > 0 ? (
            <FieldMapPreview
              geojson={fieldGeojson}
              isArable={isArable}
              fieldIds={fields.map((field) => field.guid)}
            />
          ) : (
            <MapPlaceholderText variant="body2">No Fields</MapPlaceholderText>
          )}
        </MapPreviewContainer>
        <Typography variant="body1" fontWeight="medium" gutterBottom>
          {title}
        </Typography>
        {renderMetrics()}
      </CardContent>
    </FieldCard>
  );
};

export default function FieldTypeCards() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const grainFields = useAppSelector(getGrainFields);
  const arableFields = useAppSelector(getArableFields);
  const silageFields = useAppSelector(getAllFieldsForSilage);

  const grainMetrics = useAppSelector(getGrainFieldMetrics);
  const arableMetrics = useAppSelector(getArableFieldMetrics);
  const silageMetrics = useAppSelector(getSilageFieldMetrics);

  const createFieldGeoms = (fields: BaseField[]): FeatureCollection | null => {
    if (!fields || fields.length === 0) return null;

    const features = fields.map((field) => ({
      ...field.geom,
      properties: {
        ...field.geom.properties,
        id: field.guid,
      },
    }));

    return {
      type: "FeatureCollection",
      features: features,
    } as FeatureCollection;
  };

  const handleCardClick = (fields: BaseField[], title: string) => {
    const geoms = createFieldGeoms(fields);
    if (geoms && geoms.features.length > 0) {
      mixpanel.track("Field type card clicked", { fieldType: title });
      const fieldTypeKey =
        title === t("fieldTypeCards.grain.title")
          ? "grain"
          : title === t("fieldTypeCards.arable.title")
          ? "arable"
          : title === t("fieldTypeCards.silage.title")
          ? "silage"
          : "";
      history.push(`/fields-list/${fieldTypeKey}`);
    }
  };

  const handleAddMoreFields = () => {
    mixpanel.track("Add more fields clicked");
    dispatch(setIsEditing());
    history.push(`/add-field/enter-details`);
  };

  const fieldTypes = [
    {
      title: t("fieldTypeCards.grain.title"),
      fields: grainFields.filter(Boolean) as BaseField[],
      metrics: grainMetrics,
    },
    {
      title: t("fieldTypeCards.arable.title"),
      fields: arableFields.filter(Boolean) as BaseField[],
      metrics: arableMetrics,
    },
    {
      title: t("fieldTypeCards.silage.title"),
      fields: silageFields.filter(Boolean) as BaseField[],
      metrics: silageMetrics,
    },
  ].filter((fieldType) => fieldType.fields.length > 0);

  return (
    <Box>
      <ScrollContainer>
        {fieldTypes.map((fieldType) => (
          <FieldTypeCard
            key={fieldType.title}
            title={fieldType.title}
            fields={fieldType.fields}
            metrics={fieldType.metrics}
            onCardClick={() =>
              handleCardClick(fieldType.fields, fieldType.title)
            }
          />
        ))}
      </ScrollContainer>
    </Box>
  );
}
